import { Button, Col, Form, Modal, ModalProps, Row, Select, Space } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { CheckOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';

import { Feature } from '../../../shared/models/features';
import { translationNamespace } from '../../constants/translation-resources';
import { useFindUsersQuery } from '../../data/queries/user-queries';
import { noDuplicatesRule } from '../../../shared/utils/form-rules';
import { UserStatuses } from '../../../shared/constants/user';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';

enum AssignTenantUserFormField {
  USERS = 'users',
}

export interface AssignTenantUserFormValues {
  [AssignTenantUserFormField.USERS]: number[];
}

interface AssignTenantUserModalProps extends ModalProps {
  onSubmit?: (values: AssignTenantUserFormValues, resetForm: () => void) => void;
  onInvite?: () => void;
}

const userEmailsInitialValue: string[] = [''];

export const AssignTenantUserModal: FC<AssignTenantUserModalProps> = ({
  onSubmit,
  onInvite,
  onOk,
  onCancel,
  okButtonProps,
  ...props
}) => {
  const permissions = useSelector(permissionSelector);
  const { t } = useTranslation(translationNamespace);
  const [form] = Form.useForm<AssignTenantUserFormValues>();
  const [searchValue, setSearchValue] = useState<string>();
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>();
  const { data, isLoading } = useFindUsersQuery(
    {
      page: 1,
      pageSize: 25,
      searchValue: debouncedSearchValue,
      attributes: ['id', 'email', 'status'],
    },
    {
      groups: {
        attributes: ['id', 'name', 'customerId'],
      },
    },
    props.open
  );

  const usersSelectOptions = useMemo(() => {
    return data?.resources
      ?.filter(user => user.status === UserStatuses.APPROVED || user.status === UserStatuses.PASSWORD_REQUIRED)
      .map((user, index) => (
        <Select.Option key={index} value={user.id}>
          {user.groups.length > 0
            ? `${user.email} - ${t('tenants.single.assignUserModal.alreadyAssigned')}`
            : user.email}
        </Select.Option>
      ));
  }, [data?.resources, t]);

  const confirmButtonProps: ModalProps['okButtonProps'] = {
    ...okButtonProps,
    icon: <CheckOutlined />,
    type: 'primary',
    htmlType: 'submit',
  };

  const clearSearchValue = () => {
    setSearchValue('');
    setDebouncedSearchValue('');
  };

  const triggerSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    form.submit();
    clearSearchValue();
    onOk?.(event);
  };

  const submit = (values: AssignTenantUserFormValues) => {
    onSubmit?.(values, form.resetFields);
  };

  const handleInvite = () => {
    onInvite?.();
  };

  const applySearch = (value: string) => {
    setSearchValue(value);
    setDebouncedSearchValue(value);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    form.resetFields();
    clearSearchValue();
    onCancel(e);
  };

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
    },
    3000,
    [searchValue]
  );

  return (
    <Modal
      {...props}
      centered
      title={t('tenants.single.assignUserModal.title')}
      okText={t(`${Feature.SHARED}:confirm`)}
      cancelText={t(`${Feature.SHARED}:cancel`)}
      okButtonProps={confirmButtonProps}
      onOk={triggerSubmit}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button htmlType="button" onClick={handleCancel}>
            {t(`${Feature.SHARED}:cancel`)}
          </Button>
          {permissions.USER.CREATE && (
            <Button htmlType="button" onClick={handleInvite}>
              {t('tenants.single.assignUserModal.invite')}
            </Button>
          )}
          <Button {...confirmButtonProps} onClick={triggerSubmit}>
            {t(`${Feature.SHARED}:confirm`)}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="horizontal" requiredMark={false} onFinish={submit}>
        <Form.List name={AssignTenantUserFormField.USERS} initialValue={userEmailsInitialValue}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Form.Item key={field.key}>
                  <Row gutter={[16, 24]} align={'middle'}>
                    <Col span={22}>
                      <Form.Item
                        {...field}
                        label={t(`${Feature.SHARED}:email.label`)}
                        name={field.name}
                        css={noMarginBottomStyles}
                        rules={[
                          { required: true },
                          noDuplicatesRule(form, AssignTenantUserFormField.USERS, t('tenants.shared.duplicateWarning')),
                        ]}
                      >
                        <Select
                          showSearch
                          filterOption={false}
                          loading={isLoading}
                          onSearch={applySearch}
                          onSelect={clearSearchValue}
                        >
                          {usersSelectOptions}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col span={2} css={removeIconStyles}>
                      {index !== 0 && fields.length > 1 && (
                        <MinusCircleOutlined
                          onClick={() => {
                            remove(field.name);
                            setTimeout(() => {
                              form.validateFields();
                            }, 0);
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                </Form.Item>
              ))}
              <Form.Item>
                <StyledButton type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('tenants.shared.addUser')}
                </StyledButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

const noMarginBottomStyles = css`
  margin-bottom: 0;
`;

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 180px;
`;

const removeIconStyles = css`
  padding: 9px 8px;
  display: flex;
  align-self: flex-start;
`;
